import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { ErrorMessages, SpinnerLoading } from '@/core/components'
import { omanProcessingPaymentAtom, omanProcessingPaymentModalOpenAtom, omanCheckoutErrorsAtom } from '../../../stores/oman.store'
import type { ProcessingPaymentState, ProcessingPaymentStep, StepStyle } from '../../../types/oman.types'

// ─── Icons ─────────────────────────────────────────────────────────────────

const CheckGreenIcon = () => (
    <Box
        component="svg"
        width="35"
        height="35"
        viewBox="0 0 35 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{ flexShrink: 0 }}
        data-testid="processing-payment-modal-box-path-1">
        <path
            d="M17.5 0C7.8505 0 0 7.8505 0 17.5C0 27.1495 7.8505 35 17.5 35C27.1495 35 35 27.1495 35 17.5C35 7.8505 27.1495 0 17.5 0ZM17.5 31.8182C9.60485 31.8182 3.18182 25.395 3.18182 17.5C3.18182 9.60495 9.60485 3.18182 17.5 3.18182C25.3952 3.18182 31.8182 9.60495 31.8182 17.5C31.8182 25.395 25.395 31.8182 17.5 31.8182Z"
            fill="#03C302"
            data-testid="processing-payment-modal-path-1"
        />
        <path
            d="M24.0624 11.3121L15.0627 20.3117L10.938 16.1869C10.3168 15.5657 9.30945 15.5656 8.68815 16.1868C8.06684 16.8081 8.06684 17.8153 8.68815 18.4366L13.9377 23.6864C14.2361 23.9848 14.6407 24.1524 15.0626 24.1524C15.0627 24.1524 15.0626 24.1524 15.0627 24.1524C15.4846 24.1524 15.8892 23.9848 16.1876 23.6865L26.3122 13.5621C26.9335 12.9408 26.9335 11.9335 26.3122 11.3122C25.6909 10.6909 24.6837 10.6908 24.0624 11.3121Z"
            fill="#03C302"
            data-testid="processing-payment-modal-path-2"
        />
    </Box>
)

const CrossIcon = () => (
    <Box
        component="svg"
        width="35"
        height="35"
        viewBox="0 0 35 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{ flexShrink: 0 }}
        data-testid="processing-payment-modal-box-path-2">
        <path
            d="M17.5 0C7.8505 0 0 7.8505 0 17.5C0 27.1495 7.8505 35 17.5 35C27.1495 35 35 27.1495 35 17.5C35 7.8505 27.1495 0 17.5 0ZM17.5 31.8182C9.60485 31.8182 3.18182 25.395 3.18182 17.5C3.18182 9.60495 9.60485 3.18182 17.5 3.18182C25.3952 3.18182 31.8182 9.60495 31.8182 17.5C31.8182 25.395 25.395 31.8182 17.5 31.8182Z"
            fill="#FE5D7C"
            data-testid="processing-payment-modal-path-3"
        />
        <path
            d="M23.8645 11.4215C23.3021 10.8595 22.3908 10.8595 21.8285 11.4215L17.1087 16.1411L12.4577 11.4899C11.8954 10.9278 10.9841 10.9278 10.4217 11.4899C9.85945 12.0523 9.85945 12.9636 10.4217 13.5258L15.0727 18.1769L10.49 22.7597C9.92778 23.3221 9.92778 24.2335 10.49 24.7957C10.7712 25.0767 11.1395 25.2172 11.5081 25.2172C11.8764 25.2172 12.245 25.0767 12.5262 24.7957L17.1087 20.2131L21.7598 24.8642C22.041 25.1452 22.4094 25.2857 22.7779 25.2857C23.1463 25.2857 23.5148 25.1452 23.796 24.8642C24.3582 24.3018 24.3582 23.3904 23.796 22.8282L19.1449 18.1771L23.8645 13.4575C24.4267 12.8951 24.4267 11.9837 23.8645 11.4215Z"
            fill="#FE5D7C"
            data-testid="processing-payment-modal-path-4"
        />
    </Box>
)

// ─── Stepper config ────────────────────────────────────────────────────────
//
// `STEP_STYLE` is an array of step entries, one per API field. Each entry
// carries the field name (discriminator), translation key, status→config
// mapping, and the status values that count as terminal failures. Iterating
// the array directly drives both rendering and `hasAnyFailure`.
const StepSpinner = () => <SpinnerLoading size={35} thickness={4} color="#ffffff" fullScreen={false} data-testid="processing-payment-modal-spinner-loading" />

const PROCESSING: StepStyle = { suffix: '_processing', color: '#ffffff', Icon: StepSpinner }
const SUCCESS: StepStyle = { suffix: '_success', color: '#ffffff', Icon: CheckGreenIcon }
const FAILED: StepStyle = { suffix: '_failed', color: '#ffffff', Icon: CrossIcon }

const processingPaymentSteps: ProcessingPaymentStep[] = [
    {
        field: 'hydra_status',
        translationKey: 'prepare_data',
        failures: ['fail', 'timeout'],
        configs: {
            '': PROCESSING,
            processing: PROCESSING,
            success: SUCCESS,
            fail: FAILED,
            timeout: FAILED
        }
    },
    {
        field: 'payment_status',
        translationKey: 'processing_payment',
        failures: ['fail'],
        configs: {
            waiting: PROCESSING,
            success: SUCCESS,
            fail: FAILED
        }
    }
]

// ─── Sub-components ────────────────────────────────────────────────────────

const ProcessingPaymentTitle = () => {
    const { translate } = useTranslate()
    return (
        <Typography
            variant="h6"
            sx={{
                fontFamily: "'Poppins', 'Noto Sans Thai', sans-serif",
                fontSize: 24,
                fontWeight: 600,
                color: '#ffffff',
                textAlign: 'left'
            }}
            data-testid="processing-payment-modal-typography-hydra-payment-processing">
            {translate('hydra_payment_processing')}
        </Typography>
    )
}

const CloseButton = ({ onClose }: { onClose: () => void }) => {
    const { translate } = useTranslate()
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }} data-testid="processing-payment-modal-box-button-button">
            <Button
                type="button"
                variant="contained"
                disableRipple
                disableElevation
                disableFocusRipple
                disableTouchRipple
                onClick={onClose}
                sx={{
                    width: 260,
                    height: 48,
                    fontSize: 20,
                    fontFamily: "'Poppins', 'Noto Sans Thai', sans-serif",
                    fontWeight: 600,
                    letterSpacing: '-0.2px',
                    textTransform: 'none',
                    borderRadius: '99px',
                    bgcolor: '#5a8fc4',
                    boxShadow: 'none',
                    transition: 'none',
                    '&:hover, &:active, &:focus, &:focus-visible': {
                        bgcolor: '#5a8fc4',
                        boxShadow: 'none',
                        transform: 'none'
                    }
                }}
                data-testid="processing-payment-modal-button-button">
                {translate('close').toUpperCase()}
            </Button>
        </Box>
    )
}

// Hydra returns a JSON string; surface `.error.message` (or nested
// `error.body.error.message`) when present, fallback to the raw body so
// nothing is lost.
const extractHydraErrorMessage = (body: string): string => {
    try {
        const parsed = JSON.parse(body) as {
            error?: { message?: string; body?: { error?: { message?: string } } }
            message?: string
        }
        return parsed.error?.message ?? parsed.error?.body?.error?.message ?? parsed.message ?? body
    } catch {
        return body
    }
}

const hasAnyFailure = (processingPayment: ProcessingPaymentState) => processingPaymentSteps.some(step => step.failures.includes(processingPayment[step.field]))

// ─── Main ──────────────────────────────────────────────────────────────────

const ProcessingPaymentModal = ({ onClose }: { onClose: () => void }) => {
    const { translate } = useTranslate()
    const open = useAtomValue(omanProcessingPaymentModalOpenAtom)
    const processingPayment = useAtomValue(omanProcessingPaymentAtom)
    const checkoutErrors = useAtomValue(omanCheckoutErrorsAtom)

    return (
        <Dialog
            open={open}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#003B6F',
                    borderRadius: '8px',
                    p: { xs: '20px', md: '40px' },
                    width: '100%',
                    maxWidth: '700px',
                    minHeight: 100
                }
            }}
            data-testid="processing-payment-modal-dialog">
            <Stack spacing={3} alignItems="stretch" data-testid="processing-payment-modal-stack-processing-payment-title">
                <ProcessingPaymentTitle data-testid="processing-payment-modal-processing-payment-title" />
                <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: '20px', mb: '20px', p: 0 }} data-testid="processing-payment-modal-box">
                    {processingPaymentSteps.map(step => {
                        const { suffix, color, Icon } = step.configs[processingPayment[step.field]] ?? PROCESSING
                        return (
                            <Box
                                key={step.field}
                                component="li"
                                sx={{
                                    listStyleType: 'none',
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 35px',
                                    alignItems: 'center',
                                    pb: '20px',
                                    pl: 0,
                                    '&:last-child': { pb: 0 }
                                }}
                                data-testid="processing-payment-modal-box-typography">
                                <Typography
                                    component="span"
                                    variant="body1"
                                    sx={{
                                        color,
                                        fontSize: 18,
                                        fontWeight: 600,
                                        lineHeight: '30px'
                                    }}
                                    data-testid="processing-payment-modal-typography">
                                    {translate(`${step.translationKey}${suffix}`)}
                                </Typography>
                                <Icon data-testid="processing-payment-modal-icon" />
                            </Box>
                        )
                    })}
                </Box>
                {hasAnyFailure(processingPayment) && (
                    <>
                        {processingPayment.hydra_response_body ? (
                            <ErrorMessages
                                list={[extractHydraErrorMessage(processingPayment.hydra_response_body)]}
                                data-testid="processing-payment-modal-error-messages"
                            />
                        ) : (
                            checkoutErrors.length > 0 && <ErrorMessages list={checkoutErrors} data-testid="processing-payment-modal-error-messages" />
                        )}
                        <CloseButton onClose={onClose} data-testid="processing-payment-modal-close-button" />
                    </>
                )}
            </Stack>
        </Dialog>
    )
}

export default ProcessingPaymentModal
